import React, {useEffect, useState} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  CircularProgress,
  Typography,
} from "@mui/material";
import {translate} from "@util";
import {Document} from "@core/model";
import {Download} from "@mui/icons-material";
import {api} from "@core/api";
import {useDownBreakpoint} from "@hooks";
import {useSelector} from "react-redux";
import {Selectors} from "@core/store/selectors";

const PreviewDialog = ({
  document,
  onClose,
}: {
  document: Document | undefined;
  onClose: () => void;
}) => {
  const accessToken = useSelector(Selectors.accessToken);
  const [triggerDownload] = api.useLazyDownloadDocumentQuery();

  const [open, setOpen] = useState<boolean>(false);
  const [pdfSrc, setPdfSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    setOpen(document !== undefined);
  }, [document]);

  useEffect(() => {
    let isMounted = true;

    const fetchPdf = async () => {
      if (!document) return;

      try {
        setLoading(true);
        setError(false);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/document/preview/${document.id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/pdf",
            },
          },
        );

        if (!response.ok) {
          throw new Error();
        }

        const pdfBlob = await response.blob();
        const blobUrl = URL.createObjectURL(pdfBlob);

        if (isMounted) {
          setPdfSrc(blobUrl);
        }
      } catch {
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPdf();

    return () => {
      isMounted = false;
      if (pdfSrc) {
        URL.revokeObjectURL(pdfSrc);
      }
    };
  }, [document]);

  const handleClose = () => {
    setPdfSrc(null); // Cleanup prima di chiudere
    onClose();
  };

  const isDownLg = useDownBreakpoint("lg");

  if (!document) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      fullScreen={isDownLg}>
      <DialogTitle align="center">{document.name}</DialogTitle>
      <DialogContent
        sx={{overflow: "hidden", ...(!isDownLg && {height: "70vh"})}}>
        <Stack
          textAlign="center"
          justifyContent="center"
          width="100%"
          height="100%"
          overflow="auto">
          {loading && (
            <Stack alignItems="center" spacing={2} p={4}>
              <CircularProgress />
            </Stack>
          )}
          {error && (
            <Typography color="error" p={4}>
              {translate("error.500")}
            </Typography>
          )}
          {pdfSrc && !loading && !error && (
            <embed
              src={pdfSrc}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{border: "none"}}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {translate("common.action.close")}
        </Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={() => triggerDownload(document)}
          disabled={loading || !!error}>
          {translate("common.action.download")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export {PreviewDialog};
